import { useState } from "react";
import { ImageUploader } from "../../../../../components/workorders/ImageUploader";
import { PhotoCard, SkeletonCard } from "../../../../../components/workorders/photos/PhotoCard";
import { t } from "i18next";
import { usePhotos } from "../../../../../hooks/usePhotos";
import { summarizePhotoCategories, hasTaggedPhotos, photoCategoryKey } from "./photoCategorySummary";

interface PhotosSectionProps {
  workorderId: string;
  initialPhotos: EasyTech.Photo[];
  canEdit?: boolean;
}

export function PhotosSection({ workorderId, initialPhotos, canEdit = true }: PhotosSectionProps) {
  const {
    photos,
    loadingType,
    deletePhoto,
    setPhotos,
    getDownloadUrl
  } = usePhotos({
    workorderId,
    initialPhotos,
  });

  // Active category filter per phase (null = show all). Keyed by "BEFORE" / "AFTER".
  const [categoryFilter, setCategoryFilter] = useState<Record<string, string | null>>({});

  const handlePhotoAdded = (newPhoto: EasyTech.Photo) => {
    if (!canEdit) return;
    setPhotos((prev) => [...prev, newPhoto].sort((a, b) => b.id.localeCompare(a.id)));
  };

  const handlePhotoDelete = async (photoId: string) => {
    if (!canEdit) return;
    try {
      await deletePhoto(photoId);
    } catch (error) {
      console.error("Erreur lors de la suppression de la photo:", error);
    }
  };

  // Fonction vide respectant la signature attendue
  const handlePhotoDeleteDisabled = async (_photoId: string) => { return; };

  const renderPhotoGrid = (type: "BEFORE" | "AFTER") => {
    const filteredPhotos = photos
      .filter((photo) => photo.type === type)
      .sort((a, b) => {
        const orderA = (a as EasyTech.Photo).categoryOrder;
        const orderB = (b as EasyTech.Photo).categoryOrder;
        const hasOrderA = orderA !== null && orderA !== undefined;
        const hasOrderB = orderB !== null && orderB !== undefined;
        if (hasOrderA && hasOrderB) {
          if (orderA !== orderB) return (orderA as number) - (orderB as number);
        } else if (hasOrderA) {
          return -1;
        } else if (hasOrderB) {
          return 1;
        }
        return b.id.localeCompare(a.id);
      });

    const photoTags = filteredPhotos.map((p) => ({
      categoryId: (p as EasyTech.Photo).categoryId ?? null,
      categoryLabel: (p as EasyTech.Photo).categoryLabel ?? null,
      categoryOrder: (p as EasyTech.Photo).categoryOrder ?? null
    }));
    const categoryCounts = summarizePhotoCategories(photoTags);

    const activeKey = categoryFilter[type] ?? null;
    const toggleFilter = (key: string) =>
      setCategoryFilter((prev) => ({ ...prev, [type]: prev[type] === key ? null : key }));

    const displayedPhotos = activeKey
      ? filteredPhotos.filter((p) => photoCategoryKey({
        categoryId: (p as EasyTech.Photo).categoryId ?? null,
        categoryLabel: (p as EasyTech.Photo).categoryLabel ?? null,
        categoryOrder: (p as EasyTech.Photo).categoryOrder ?? null
      }) === activeKey)
      : filteredPhotos;

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4 border-b border-gray-300 pb-4">
          <h2 className="text-xl font-bold">
            {t(type === "BEFORE" ? t("workorder:files:picturesBefore") : t("workorder:files:picturesAfter"))}
          </h2>
          {canEdit && (
            <ImageUploader
              workorderId={workorderId}
              type={type}
              buttonText={t("workorder:files:buttons:add")}
              onFileAdded={handlePhotoAdded}
              uploadType="photos"
              initialFiles={initialPhotos}
            />
          )}
        </div>

        {hasTaggedPhotos(photoTags) && (
          <div className="flex flex-wrap gap-2 mb-4">
            {categoryCounts.map((c) => {
              const isActive = activeKey === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => toggleFilter(c.key)}
                  aria-pressed={isActive}
                  title={t("workorder:files:filterByCategory", "Filter by category")}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs transition-colors cursor-pointer ${
                    isActive
                      ? "bg-blue-500 text-white"
                      : c.label
                        ? "bg-blue-gray-100 text-blue-gray-800 hover:bg-blue-gray-200"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  <span>{c.label ?? t("workorder:files:untagged", "Uncategorized")}</span>
                  <span className="font-semibold">{c.count}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Afficher le skeleton pendant l'upload */}
          {loadingType === type && (
            <SkeletonCard
              key={`skeleton-${type}`}
              className="w-full aspect-square"
            />
          )}

          {/* Afficher les photos existantes */}
          {displayedPhotos.map((photo) => (
            <PhotoCard
              key={photo.id}
              workorderId={workorderId}
              photo={photo}
              onDelete={canEdit ? handlePhotoDelete : handlePhotoDeleteDisabled}
              className="w-full h-full object-cover rounded-lg"
              type={type}
              getDownloadUrl={getDownloadUrl}
              uploadType="photos"
              canEdit={canEdit}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderPhotoGrid("BEFORE")}
      {renderPhotoGrid("AFTER")}
    </div>
  );
}